// === SAA WORKFLOW CONFIGURATION ===
// Single source of truth for the SAA workflow sequence. The PRODUCT order is
// driven by config.json (workflow.productOrder); everything else — navigation,
// the step indicator, premium iteration, policy-cost allocation, the selection
// screen — derives from getProductOrder() so a brand can reorder or exclude
// products from ONE place. The static defaults below are the fallback used
// before config.json loads (and when a brand omits productOrder).

import { WorkflowNavigationConfig, WorkflowStep } from '@sluitstraat/core';
import type { SaaProductId } from './utils/addressSharing';

/** Per-product step metadata — the single place a product's name + route live. */
const PRODUCT_STEP_META: Record<SaaProductId, { name: string; route: string }> = {
  woonhuis: { name: 'Woonhuis', route: '/workflow/woonhuis' },
  inboedel: { name: 'Inboedel', route: '/workflow/inboedel' },
  aansprakelijkheid: { name: 'Aansprakelijkheid', route: '/workflow/aansprakelijkheid' },
  rechtsbijstand: { name: 'Rechtsbijstand', route: '/workflow/rechtsbijstand' },
  ongevallen: { name: 'Ongevallen', route: '/workflow/ongevallen' },
  personenauto: { name: 'Personenauto', route: '/workflow/personenauto' },
  doorlopendereis: { name: 'Doorlopende reis', route: '/workflow/doorlopendereis' },
};

/** Fallback order (config.json workflow.productOrder overrides this at load). */
export const DEFAULT_PRODUCT_ORDER: SaaProductId[] = [
  'woonhuis',
  'inboedel',
  'aansprakelijkheid',
  'rechtsbijstand',
  'ongevallen',
  'personenauto',
  'doorlopendereis',
];

const UNIVERSAL_STEPS_BEFORE: WorkflowStep[] = [
  { id: 'product-selection', name: 'Productkeuze', route: '/workflow/product-selection', type: 'universal' },
  { id: 'client-information', name: 'Persoonlijke gegevens', route: '/workflow/client-information', type: 'universal' },
];

const UNIVERSAL_STEPS_AFTER: WorkflowStep[] = [
  { id: 'summary', name: 'Overzicht', route: '/workflow/summary', type: 'universal' },
];

export const SAA_APPLICATION_STEPS: WorkflowStep[] = [
  { id: 'payment-information', name: 'Betaalgegevens', route: '/workflow/payment-information', type: 'universal' },
  { id: 'acceptance-questions', name: 'Acceptatievragen', route: '/workflow/acceptance-questions', type: 'universal' },
  { id: 'confirmation', name: 'Bevestiging', route: '/workflow/confirmation', type: 'universal' },
];

const isSaaProduct = (id: string): id is SaaProductId =>
  (DEFAULT_PRODUCT_ORDER as readonly string[]).includes(id);

/** Normalize a raw order (from config or code) to known products, dedup, fallback if empty. */
export const resolveProductOrder = (raw: readonly string[] | undefined | null): SaaProductId[] => {
  if (!Array.isArray(raw)) return [...DEFAULT_PRODUCT_ORDER];
  const seen = new Set<SaaProductId>();
  for (const id of raw) if (isSaaProduct(id)) seen.add(id);
  return seen.size ? [...seen] : [...DEFAULT_PRODUCT_ORDER];
};

/** Build the ordered workflow step list for a given product order. */
export const buildWorkflowSteps = (order: readonly SaaProductId[]): WorkflowStep[] => [
  ...UNIVERSAL_STEPS_BEFORE,
  ...order.map((id) => ({
    id,
    name: PRODUCT_STEP_META[id].name,
    route: PRODUCT_STEP_META[id].route,
    type: 'product' as const,
    productId: id,
  })),
  ...UNIVERSAL_STEPS_AFTER,
];

/** Build the full navigation config (steps + application steps) for a product order. */
export const buildWorkflowConfig = (order: readonly SaaProductId[]): WorkflowNavigationConfig => ({
  steps: buildWorkflowSteps(order),
  applicationSteps: SAA_APPLICATION_STEPS,
});

// --- Active product order singleton -------------------------------------------------
// Mirrors core's setWorkflowConfig pattern: set once when config.json loads (App.tsx),
// read synchronously thereafter. Safe because ConfigProvider gates screen render until
// config is loaded, and every consumer reads getProductOrder() at render/call time.
let activeProductOrder: SaaProductId[] = [...DEFAULT_PRODUCT_ORDER];

export const setActiveProductOrder = (raw: readonly string[] | undefined | null): void => {
  activeProductOrder = resolveProductOrder(raw);
};

/** Canonical product order — config-driven, falls back to DEFAULT_PRODUCT_ORDER. */
export const getProductOrder = (): SaaProductId[] => activeProductOrder;

/** Full ordered page id list (workflow + application + end) for navigation math. */
export const getWorkflowPageOrder = (): string[] => [
  ...buildWorkflowSteps(activeProductOrder).map((s) => s.id),
  ...SAA_APPLICATION_STEPS.map((s) => s.id),
  'end',
];

/** Empty selection object (every configured product unselected) for screen state init. */
export const buildEmptySelection = (): Record<SaaProductId, boolean> =>
  activeProductOrder.reduce((acc, id) => {
    acc[id] = false;
    return acc;
  }, {} as Record<SaaProductId, boolean>);

// --- Static defaults (fallback before config.json loads) ----------------------------
export const SAA_WORKFLOW_STEPS: WorkflowStep[] = buildWorkflowSteps(DEFAULT_PRODUCT_ORDER);

export const saaWorkflowConfig: WorkflowNavigationConfig = buildWorkflowConfig(DEFAULT_PRODUCT_ORDER);
